import {v4 as uuid} from "uuid"
import {get, head, max} from "lodash"
import {DigitalDocumentFieldSetUp, DigitalField} from "types/tasks"

export const duplicateField = (
    fields: DigitalField[],
    fieldToDuplicate: DigitalField
): {newFields: DigitalField[]; newField: DigitalField} => {
    switch (fieldToDuplicate.type) {
        case DigitalDocumentFieldSetUp.TEXT:
        case DigitalDocumentFieldSetUp.GRADE:
        case DigitalDocumentFieldSetUp.FILE:
            return duplicateTextField(fields, fieldToDuplicate)
        case DigitalDocumentFieldSetUp.SIGNATURE:
            return duplicateSignatureField(fields, fieldToDuplicate)
        case DigitalDocumentFieldSetUp.CHECKBOX:
        case DigitalDocumentFieldSetUp.INITIAL:
            return duplicateCheckboxOrInitialField(fields, fieldToDuplicate)
        case DigitalDocumentFieldSetUp.SELECT:
            return duplicateSelectField(fields, fieldToDuplicate)
        default:
            throw new Error("Unsupported field type")
    }
}

export const getNextFieldIndex = (fields: DigitalField[], type: string) => {
    const maxCurrentIndex = max(fields.filter((field) => field.type === type).map((field) => field.fieldIndex)) || 0
    return maxCurrentIndex + 1
}

const duplicateTextField = (
    fields: DigitalField[],
    field: DigitalField
): {newFields: DigitalField[]; newField: DigitalField} => {
    const newField: DigitalField = {
        ...field,
        id: uuid(),
        uuid: uuid(),
        fieldIndex: getNextFieldIndex(fields, field.type),
        placeInformation: {
            ...field.placeInformation,
            top: field.placeInformation.top + field.placeInformation.height * 1.5
        }
    }
    return {newFields: [...fields, newField], newField}
}

const duplicateSignatureField = (
    fields: DigitalField[],
    field: DigitalField
): {newFields: DigitalField[]; newField: DigitalField} => {
    const newFields = [...fields]
    const dateField = fields.find(
        ({parentId, type}) => parentId === field.id && type === DigitalDocumentFieldSetUp.DATE_FIELD
    )
    const newField = {
        ...field,
        id: uuid(),
        uuid: uuid(),
        fieldIndex: getNextFieldIndex(fields, field.type),
        placeInformation: {
            ...field.placeInformation,
            top: field.placeInformation.top + field.placeInformation.height * 1.5
        }
    }
    newFields.push(newField)
    if (dateField) {
        const newDateField = {
            ...dateField,
            id: uuid(),
            uuid: uuid(),
            parentId: newField.id,
            parentSignatureTypeId: get(newField, "signatureTypes[0].id", null),
            placeInformation: {
                ...dateField.placeInformation,
                top: dateField.placeInformation.top + dateField.placeInformation.height * 1.5
            }
        }
        newFields.push(newDateField)
    }
    return {newFields, newField}
}

const duplicateCheckboxOrInitialField = (
    fields: DigitalField[],
    field: DigitalField
): {newFields: DigitalField[]; newField: DigitalField} => {
    const newField = {
        ...field,
        id: uuid(),
        uuid: uuid(),
        fieldIndex: getNextFieldIndex(fields, field.type),
        placeInformation: {
            ...field.placeInformation,
            top: field.placeInformation.top + field.placeInformation.height * 1.5
        }
    }
    const newChildFields = fields
        .filter((childField) => childField.parentId && childField.parentId === field.id)
        .map((childField) => {
            const oldChildFieldValue = field.fieldValues.find(({id}) => id === childField.id)
            return {
                ...childField,
                id: uuid(),
                uuid: uuid(),
                label: get(oldChildFieldValue, "label", ""),
                parentId: newField.id,
                parentSignatureTypeId: get(newField, "signatureTypes[0].id", null),
                placeInformation: {
                    ...childField.placeInformation,
                    top: childField.placeInformation.top + childField.placeInformation.height * 1.5
                }
            }
        })
    const firstChildField = {
        ...(head(field.fieldValues) as any),
        id: uuid(),
        uuid: uuid(),
        parentId: newField.id,
        parentSignatureTypeId: get(newField, "signatureTypes[0].id", null),
        placeInformation: {
            ...newField.placeInformation,
            top: newField.placeInformation.top + newField.placeInformation.height * 1.5
        }
    }
    newField.fieldValues = [firstChildField, ...newChildFields]
    const newFields = [...fields, newField, ...newChildFields]
    return {newFields, newField}
}

const duplicateSelectField = (
    fields: DigitalField[],
    field: DigitalField
): {newFields: DigitalField[]; newField: DigitalField} => {
    const newField: DigitalField = {
        ...field,
        id: uuid(),
        uuid: uuid(),
        fieldIndex: getNextFieldIndex(fields, field.type),
        fieldValues: field.fieldValues.map((item) => ({...item, id: uuid(), uuid: uuid()})),
        placeInformation: {
            ...field.placeInformation,
            top: field.placeInformation.top + field.placeInformation.height * 1.5
        }
    }
    return {newFields: [...fields, newField], newField}
}
